/**
 * PCM Audio Streamer
 * Converts browser audio to 16kHz 16-bit PCM for Azure Speech SDK
 */

export async function createPcmStreamer(stream, { onFrame, targetSampleRate = 16000, audioContext: providedContext }) {
  const audioContext = providedContext || new (window.AudioContext || window.webkitAudioContext)();
  // Debug: report input sample rate
  const DEBUG = process.env.REACT_APP_DEBUG === '1' || process.env.REACT_APP_DEBUG === 'true';
  try {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('AudioContext sampleRate:', audioContext.sampleRate);
    }
  } catch (_) {}

  // Ensure audio context is running
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }

  const source = audioContext.createMediaStreamSource(stream);

  // ScriptProcessorNode for broad browser compatibility
  const bufferSize = 2048; // tighter buffer for lower latency while staying stable
  const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);

  const inputSampleRate = audioContext.sampleRate;
  const ratio = inputSampleRate / targetSampleRate;

  // Proper downsampling with averaging to reduce aliasing
  const downsample = (input) => {
    if (targetSampleRate === inputSampleRate) {
      // Convert float32 -> int16 directly
      const outBytes = new Uint8Array(input.length * 2);
      const view = new DataView(outBytes.buffer);
      for (let i = 0; i < input.length; i++) {
        // Clamp and scale to int16, little-endian
        const s = Math.max(-1, Math.min(1, input[i]));
        const v = s < 0 ? Math.round(s * 32768) : Math.round(s * 32767);
        view.setInt16(i * 2, v, true);
      }
      return outBytes;
    }

    const newLength = Math.round(input.length / ratio);
    const outBytes = new Uint8Array(newLength * 2);
    const view = new DataView(outBytes.buffer);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < newLength) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
      let sum = 0;
      let count = 0;
      for (let i = offsetBuffer; i < nextOffsetBuffer && i < input.length; i++) {
        sum += input[i];
        count++;
      }
      const sample = count > 0 ? sum / count : 0;
      const s = Math.max(-1, Math.min(1, sample));
      const v = s < 0 ? Math.round(s * 32768) : Math.round(s * 32767);
      view.setInt16(offsetResult * 2, v, true);
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }
    return outBytes;
  };

  processor.onaudioprocess = (e) => {
    try {
      const inputData = e.inputBuffer.getChannelData(0);
      if (!inputData || inputData.length === 0) return;
      const bytes = downsample(inputData);
      if (bytes && bytes.length) {
        onFrame(bytes);
      }
    } catch (err) {
      // Swallow to avoid breaking the audio graph
      // eslint-disable-next-line no-console
      console.warn('PCM processing error:', err);
    }
  };

  // Connect the audio graph
  source.connect(processor);
  // Some browsers require connecting to destination for processing callbacks
  processor.connect(audioContext.destination);

  return {
    close: () => {
      try {
        processor.disconnect();
        source.disconnect();
        if (!providedContext) {
          audioContext.close();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Error closing audio stream:', e);
      }
    }
  };
}
